import React, { useState, useMemo } from "react";
import { eur, api } from "@/lib/api";
import {
  Storefront,
  Users,
  Coin,
  Receipt,
  MagnifyingGlass,
  EnvelopeSimple,
  Phone,
  PencilSimple,
  Check,
  Copy,
  ArrowRight,
  Buildings,
  Tag,
  Funnel,
  X,
  FloppyDisk,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function ProveedoresView({ proveedores = [], onRefresh, onNavigate }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("monto"); // "monto" | "facturas" | "nombre"
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Estados del formulario de edición
  const [editNombre, setEditNombre] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editTelefono, setEditTelefono] = useState("");
  const [editCategoria, setEditCategoria] = useState("");

  // KPIs globales
  const stats = useMemo(() => {
    const totalProveedores = proveedores.length;
    const totalGasto = proveedores.reduce((acc, p) => acc + (p.total_acumulado || 0), 0);
    const totalFacturas = proveedores.reduce((acc, p) => acc + (p.total_facturas || 0), 0);

    const topProveedor = [...proveedores].sort(
      (a, b) => (b.total_acumulado || 0) - (a.total_acumulado || 0)
    )[0];

    return {
      totalProveedores,
      totalGasto,
      totalFacturas,
      topProveedorName: topProveedor?.nombre_normalizado || "—",
      topProveedorTotal: topProveedor?.total_acumulado || 0,
    };
  }, [proveedores]);

  // Filtrado y ordenación
  const filteredProveedores = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    let list = proveedores.filter((p) => {
      const nombre = (p.nombre_normalizado || "").toLowerCase();
      const cif = (p.cif_nif || "").toLowerCase();
      const email = (p.email || "").toLowerCase();
      const tel = (p.telefono || "").toLowerCase();
      const alias = (p.alias || []).join(" ").toLowerCase();
      return (
        nombre.includes(term) ||
        cif.includes(term) ||
        email.includes(term) ||
        tel.includes(term) ||
        alias.includes(term)
      );
    });

    list.sort((a, b) => {
      if (sortBy === "monto") return (b.total_acumulado || 0) - (a.total_acumulado || 0);
      if (sortBy === "facturas") return (b.total_facturas || 0) - (a.total_facturas || 0);
      if (sortBy === "nombre")
        return (a.nombre_normalizado || "").localeCompare(b.nombre_normalizado || "");
      return 0;
    });

    return list;
  }, [proveedores, searchTerm, sortBy]);

  // Copiar al portapapeles
  const handleCopy = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Abrir modal de edición
  const handleOpenEdit = (p) => {
    setEditingProveedor(p);
    setEditNombre(p.nombre_normalizado || "");
    setEditEmail(p.email || "");
    setEditTelefono(p.telefono || "");
    setEditCategoria(p.categoria || "");
  };

  // Guardar cambios del proveedor
  const handleSaveProveedor = async (e) => {
    e.preventDefault();
    if (!editingProveedor) return;
    setSaving(true);
    try {
      await api.updateProveedorDetails(editingProveedor.cif_nif, {
        nombre_normalizado: editNombre,
        email: editEmail,
        telefono: editTelefono,
        categoria: editCategoria,
      });
      toast.success("Proveedor actualizado correctamente");
      setEditingProveedor(null);
      onRefresh?.();
    } catch (err) {
      toast.error("Error al actualizar proveedor: " + (err.message || ""));
    } finally {
      setSaving(false);
    }
  };

  // Generar color de avatar por nombre
  const getAvatarColor = (name = "") => {
    const colors = [
      "bg-emerald-50 text-emerald-700 border-emerald-200",
      "bg-blue-50 text-blue-700 border-blue-200",
      "bg-purple-50 text-purple-700 border-purple-200",
      "bg-amber-50 text-amber-700 border-amber-200",
      "bg-rose-50 text-rose-700 border-rose-200",
      "bg-indigo-50 text-indigo-700 border-indigo-200",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="animate-fadein space-y-4 sm:space-y-6" data-testid="proveedores-view">
      {/* KPIs superiores */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Total Proveedores</span>
            <Users size={18} className="text-slate-400" />
          </div>
          <div className="mt-2 text-2xl sm:text-3xl font-display font-bold text-slate-900 kpi-num">
            {stats.totalProveedores}
          </div>
          <div className="mt-1 text-xs text-slate-400">Proveedores con historial</div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Total Facturas</span>
            <Receipt size={18} className="text-slate-400" />
          </div>
          <div className="mt-2 text-2xl sm:text-3xl font-display font-bold text-slate-900 kpi-num">
            {stats.totalFacturas}
          </div>
          <div className="mt-1 text-xs text-slate-400">Documentos emitidos</div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Gasto Total</span>
            <Coin size={18} className="text-emerald-600" />
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-display font-bold text-emerald-600 kpi-num">
            {eur(stats.totalGasto)}
          </div>
          <div className="mt-1 text-xs text-slate-400">Acumulado proveedores</div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Mayor Proveedor</span>
            <Storefront size={18} className="text-slate-400" />
          </div>
          <div className="mt-2 text-sm sm:text-base font-bold text-slate-800 truncate" title={stats.topProveedorName}>
            {stats.topProveedorName}
          </div>
          <div className="mt-1 text-xs font-mono font-medium text-slate-500">
            {eur(stats.topProveedorTotal)}
          </div>
        </div>
      </div>

      {/* Barra de Búsqueda y Ordenación */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Buscar por proveedor, CIF, email o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <span className="text-xs text-slate-400 font-medium hidden sm:inline flex items-center gap-1">
            <Funnel size={14} /> Ordenar:
          </span>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50 text-xs">
            <button
              onClick={() => setSortBy("monto")}
              className={`px-2.5 py-1.5 rounded-md font-medium transition-colors ${
                sortBy === "monto" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Mayor Importe
            </button>
            <button
              onClick={() => setSortBy("facturas")}
              className={`px-2.5 py-1.5 rounded-md font-medium transition-colors ${
                sortBy === "facturas" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Nº Facturas
            </button>
            <button
              onClick={() => setSortBy("nombre")}
              className={`px-2.5 py-1.5 rounded-md font-medium transition-colors ${
                sortBy === "nombre" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Nombre
            </button>
          </div>
        </div>
      </div>

      {/* Tabla Principal de Proveedores */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-sm min-w-[800px]" data-testid="proveedores-table">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-5 py-3.5">Proveedor & CIF</th>
                <th className="px-4 py-3.5 text-center">Total Facturas</th>
                <th className="px-4 py-3.5 text-right">Monto Total</th>
                <th className="px-5 py-3.5">Datos de Contacto (Tel / Email)</th>
                <th className="px-4 py-3.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProveedores.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-slate-400">
                    <Storefront size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="font-medium text-slate-600">No se encontraron proveedores</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {searchTerm
                        ? "Prueba a cambiar el término de búsqueda."
                        : "Los proveedores se registran automáticamente al procesar facturas."}
                    </p>
                  </td>
                </tr>
              )}

              {filteredProveedores.map((p) => {
                const initial = (p.nombre_normalizado || "P").charAt(0).toUpperCase();
                const avatarStyle = getAvatarColor(p.nombre_normalizado);

                return (
                  <tr
                    key={p.cif_nif || p.id}
                    className="hover:bg-slate-50/80 transition-colors group"
                    data-testid={`proveedor-row-${p.cif_nif}`}
                  >
                    {/* Proveedor & CIF */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-9 w-9 rounded-lg flex items-center justify-center font-bold text-sm border shrink-0 shadow-xs ${avatarStyle}`}
                        >
                          {initial}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 flex items-center gap-2">
                            <span className="truncate">{p.nombre_normalizado}</span>
                            {p.categoria && (
                              <span className="hidden sm:inline text-[10px] font-medium bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200">
                                {p.categoria}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="font-mono text-xs text-slate-500 font-medium">
                              CIF: {p.cif_nif || "—"}
                            </span>
                            {p.alias && p.alias.length > 1 && (
                              <span
                                className="text-[10px] text-slate-400 truncate max-w-[160px]"
                                title={`Alias: ${p.alias.join(", ")}`}
                              >
                                · {p.alias.length} alias
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Total Facturas */}
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                        {p.total_facturas || 0} {p.total_facturas === 1 ? "factura" : "facturas"}
                      </span>
                    </td>

                    {/* Monto Total */}
                    <td className="px-4 py-3.5 text-right">
                      <div className="font-mono font-bold text-slate-900 text-sm sm:text-base kpi-num">
                        {eur(p.total_acumulado || 0)}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        {p.total_facturas > 0
                          ? `media ${eur((p.total_acumulado || 0) / p.total_facturas)}`
                          : "sin facturas"}
                      </div>
                    </td>

                    {/* Datos de Contacto */}
                    <td className="px-5 py-3.5">
                      <div className="space-y-1 text-xs">
                        {/* Email */}
                        <div className="flex items-center gap-2">
                          <EnvelopeSimple size={14} className="text-slate-400 shrink-0" />
                          {p.email ? (
                            <div className="flex items-center gap-1.5 min-w-0">
                              <a
                                href={`mailto:${p.email}`}
                                className="font-mono text-slate-700 hover:text-blue-600 hover:underline truncate max-w-[200px]"
                                title={`Enviar correo a ${p.email}`}
                              >
                                {p.email}
                              </a>
                              <button
                                onClick={() => handleCopy(p.email, `email-${p.cif_nif}`)}
                                className="text-slate-400 hover:text-slate-600 p-0.5 rounded transition-colors"
                                title="Copiar email"
                              >
                                {copiedId === `email-${p.cif_nif}` ? (
                                  <Check size={12} className="text-emerald-600" />
                                ) : (
                                  <Copy size={12} />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">No registrado</span>
                          )}
                        </div>

                        {/* Teléfono */}
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-slate-400 shrink-0" />
                          {p.telefono && p.telefono !== "—" ? (
                            <div className="flex items-center gap-1.5">
                              <a
                                href={`tel:${p.telefono.replace(/\s+/g, "")}`}
                                className="font-mono text-slate-700 hover:text-emerald-600 hover:underline"
                                title={`Llamar a ${p.telefono}`}
                              >
                                {p.telefono}
                              </a>
                              <button
                                onClick={() => handleCopy(p.telefono, `tel-${p.cif_nif}`)}
                                className="text-slate-400 hover:text-slate-600 p-0.5 rounded transition-colors"
                                title="Copiar teléfono"
                              >
                                {copiedId === `tel-${p.cif_nif}` ? (
                                  <Check size={12} className="text-emerald-600" />
                                ) : (
                                  <Copy size={12} />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">No registrado</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(p)}
                          className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900 gap-1"
                          title="Editar datos de contacto"
                        >
                          <PencilSimple size={14} />
                          <span className="hidden sm:inline">Editar</span>
                        </Button>

                        {onNavigate && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onNavigate("registro")}
                            className="h-8 px-2.5 text-xs border-slate-200 text-slate-700 hover:bg-slate-50 gap-1"
                            title="Ver facturas en el Registro"
                          >
                            <span className="hidden sm:inline">Facturas</span>
                            <ArrowRight size={13} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Edición de Proveedor */}
      {editingProveedor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-150">
          <div
            className="bg-white w-full max-w-md rounded-xl border border-slate-200 shadow-xl overflow-hidden animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                  <Buildings size={18} weight="bold" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Editar Proveedor</h3>
                  <p className="text-[11px] font-mono text-slate-400">CIF: {editingProveedor.cif_nif}</p>
                </div>
              </div>
              <button
                onClick={() => setEditingProveedor(null)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveProveedor} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Nombre Comercial Normalizado
                </label>
                <input
                  type="text"
                  required
                  value={editNombre}
                  onChange={(e) => setEditNombre(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                  <EnvelopeSimple size={14} className="text-slate-400" />
                  Email de Facturación / Contacto
                </label>
                <input
                  type="email"
                  placeholder="ej: facturacion@proveedor.es"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                  <Phone size={14} className="text-slate-400" />
                  Teléfono de Contacto
                </label>
                <input
                  type="text"
                  placeholder="ej: +34 91 234 56 78"
                  value={editTelefono}
                  onChange={(e) => setEditTelefono(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                  <Tag size={14} className="text-slate-400" />
                  Categoría o Tipo de Suministro
                </label>
                <input
                  type="text"
                  placeholder="ej: Alimentación, Electricidad, Bebidas, Servicios"
                  value={editCategoria}
                  onChange={(e) => setEditCategoria(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingProveedor(null)}
                  className="text-slate-600"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  size="sm"
                  className="gap-1.5 bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
                >
                  <FloppyDisk size={16} />
                  {saving ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
